import { FC, useState } from 'react'
import { TabsContent } from '../../components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { Shield, UserPlus, Loader2, AlertCircle, CheckCircle, Trash2, Users } from 'lucide-react'
import { toast } from 'sonner'
import { useFunctions } from '../../hooks/use-firebase'
import { httpsCallable } from 'firebase/functions'
import { SubscriptionTypeEnum } from '../../lib/enums'

const AdminSettingsTab: FC = () => {
  const functions = useFunctions()
  const [loading, setLoading] = useState(false)
  const [listLoading, setListLoading] = useState(false)

  // Form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  //const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTypeEnum>(SubscriptionTypeEnum.Basic)

  // User list state
  const [users, setUsers] = useState<any[]>([])
  const [showUserList, setShowUserList] = useState(false)

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!email || !email.includes('@')) {
      newErrors.email = 'Valid email is required'
    }

    if (!password || password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }

    if (!firstName || firstName.trim().length === 0) {
      newErrors.firstName = 'First name is required'
    }

    if (!lastName || lastName.trim().length === 0) {
      newErrors.lastName = 'Last name is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleCreateUser = async () => {
    if (!validateForm()) {
      toast.error('Please fix the form errors')
      return
    }

    setLoading(true)
    try {
      const createUser = httpsCallable(functions, 'createUser')
      const result = await createUser({
        email,
        password,
        firstName,
        lastName,
        //subscriptionTier,
      })

      const data = result.data as any

      if (data.success) {
        toast.success(`User created successfully: ${data.email}`)

        // Reset form
        setEmail('')
        setPassword('')
        setFirstName('')
        setLastName('')
        //setSubscriptionTier(SubscriptionTypeEnum.Basic)
        setErrors({})
      } else {
        toast.error('Failed to create user')
      }
    } catch (error: any) {
      console.error('Error creating user:', error)
      const errorMessage = error.message || 'Failed to create user'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleListUsers = async () => {
    setListLoading(true)
    try {
      const listUsers = httpsCallable(functions, 'listUsers')
      const result = await listUsers({ maxResults: 100 })

      const data = result.data as any

      if (data.users) {
        setUsers(data.users)
        setShowUserList(true)
        toast.success(`Found ${data.users.length} users`)
      } else {
        toast.error('Failed to fetch users')
      }
    } catch (error: any) {
      console.error('Error listing users:', error)
      toast.error(error.message || 'Failed to fetch users')
    } finally {
      setListLoading(false)
    }
  }

  const handleDeleteUser = async (uid: string, userEmail: string) => {
    /*const consent = confirm(`Are you sure you want to delete user ${userEmail}? This action cannot be undone.`)
    if (consent) {
      return
    }*/

    try {
      const deleteUser = httpsCallable(functions, 'deleteUser')
      const result = await deleteUser({ uid })

      const data = result.data as any

      if (data.success) {
        toast.success(`User ${userEmail} deleted successfully`)

        // Remove from local list
        setUsers(users.filter(u => u.uid !== uid))
      } else {
        toast.error('Failed to delete user')
      }
    } catch (error: any) {
      console.error('Error deleting user:', error)
      toast.error(error.message || 'Failed to delete user')
    }
  }

  return (
    <TabsContent value="admin_settings" className="space-y-6">
      {/* Admin Badge */}
      <Card className="border-sumoPrimary bg-sumoPrimary/5 dark:bg-sumoPrimary/10">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-sumoPrimary" />
            <div>
              <p className="font-semibold text-neutral900 dark:text-neutral100">Admin Access</p>
              <p className="text-sm text-neutral600 dark:text-neutral400">
                You have admin privileges. Use these tools responsibly.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create User Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Create New User
          </CardTitle>
          <CardDescription>
            Create a new user account with email and password
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={errors.email ? 'border-error500' : ''}
            />
            {errors.email && (
              <p className="text-sm text-error500 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.email}
              </p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <Input
              id="password"
              type="password"
              placeholder="Minimum 6 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={errors.password ? 'border-error500' : ''}
            />
            {errors.password && (
              <p className="text-sm text-error500 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.password}
              </p>
            )}
          </div>

          {/* First Name */}
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name *</Label>
            <Input
              id="firstName"
              type="text"
              placeholder="John"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={errors.firstName ? 'border-error500' : ''}
            />
            {errors.firstName && (
              <p className="text-sm text-error500 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.firstName}
              </p>
            )}
          </div>

          {/* Last Name */}
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name *</Label>
            <Input
              id="lastName"
              type="text"
              placeholder="Doe"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={errors.lastName ? 'border-error500' : ''}
            />
            {errors.lastName && (
              <p className="text-sm text-error500 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.lastName}
              </p>
            )}
          </div>

          {/* Subscription Tier 
          <div className="space-y-2">
            <Label htmlFor="subscriptionTier">Subscription Tier</Label>
            <Select value={subscriptionTier} onValueChange={(value) => setSubscriptionTier(value as SubscriptionTypeEnum)}>
              <SelectTrigger>
                <SelectValue placeholder="Select tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SubscriptionTypeEnum.Basic}>Basic</SelectItem>
                <SelectItem value={SubscriptionTypeEnum.Premium}>Premium</SelectItem>
                <SelectItem value={SubscriptionTypeEnum.Enterprise}>Enterprise</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-neutral500 dark:text-neutral400">
              User will be created with this subscription tier
            </p>
          </div>*/}

          {/* Create Button */}
          <Button
            variant="primaryGlow"
            size="lg"
            className="w-full"
            onClick={handleCreateUser}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating User...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                Create User
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* User Management Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            User Management
          </CardTitle>
          <CardDescription>
            View and manage all users in the system
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            variant="outline"
            size="lg"
            className="w-full"
            onClick={handleListUsers}
            disabled={listLoading}
          >
            {listLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading Users...
              </>
            ) : (
              <>
                <Users className="w-4 h-4" />
                List All Users
              </>
            )}
          </Button>

          {/* User List */}
          {showUserList && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-neutral700 dark:text-neutral300">
                {users.length} users found
              </p>
              <div className="max-h-96 overflow-y-auto space-y-2">
                {users.map((user) => (
                  <div
                    key={user.uid}
                    className="p-3 border border-neutral200 dark:border-neutral700 rounded-lg flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-neutral900 dark:text-neutral100">
                        {user.email}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-1 bg-neutral100 dark:bg-neutral800 rounded">
                          {user.customClaims?.subscription || 'basic'}
                        </span>
                        {user.emailVerified && (
                          <CheckCircle className="w-4 h-4 text-success500" />
                        )}
                        {user.disabled && (
                          <span className="text-xs px-2 py-1 bg-error100 dark:bg-error900 text-error700 dark:text-error300 rounded">
                            Disabled
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-neutral500 dark:text-neutral400 mt-1">
                        Created: {new Date(user.creationTime).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteUser(user.uid, user.email)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  )
}

export default AdminSettingsTab
